<template>
    <div class="offcanvas offcanvas-end" :class="offcanvasStateClass" id="quickReportExportOffcanvas">
      <div class="offcanvas-header">
        <button class="btn-back" type="button" @click="close">
          <IconArrowBack/>
          {{ $t('goBack') }}
        </button>
        <h3 class="offcanvas-title d-block">{{ $t('createClinic.exportQuickReport.title') }}</h3>
      </div>
  
      <div class="offcanvas-body">
        <p>{{ $t('createClinic.exportQuickReport.description') }}</p>
  
        <div class="download-dates">
          <div class="form-floating">
            <date-picker
              v-model="startDate"
              :locale="locale"
              value-type="date"
              :selectText="$t('select')"
              :cancelText="$t('cancel')"
              :format="$t('dateFormatFns')"
              :inputFormat="$t('dateFormatFns')"
              :enableTimePicker="false"
              data-test="datePickerModalDownload">
            </date-picker>
            <label for="">{{ $t('exam.selectStartDate') }}</label>
          </div>
          <div class="form-floating">
            <date-picker
              v-model="endDate"
              :locale="locale"
              value-type="date"
              :selectText="$t('select')"
              :cancelText="$t('cancel')"
              :format="$t('dateFormatFns')"
              :inputFormat="$t('dateFormatFns')"
              :enableTimePicker="false"
              data-test="datePickerModalDownload">
            </date-picker>
            <label for="">{{ $t('exam.selectEndDate') }}</label>
          </div>
        </div>
      </div>

      <div class="offcanvas-footer">
        <button type="button"
          class="btn btn-success"
          :class="okButtonClass"
          :disabled="!startDate || !endDate || loading"
          @click="doExport">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          {{ $t('createClinic.exportQuickReport.downloadCSV') }}
        </button>
      </div>
      
      <teleport to="body">
        <div v-if="offcanvasStateClass" class="offcanvas-backdrop fade show" @click="close"></div>
      </teleport>
    </div>
  </template>

<style lang="scss" scoped>
.offcanvas {
  .offcanvas-title,
  h3 {
    color:                              var(--Brand-Primary, #1B294A);
    font-size:                          38px;
    font-weight:                        300;
    line-height:                        normal;
    margin:                             26px 0;
  }

  h3 {
    font-size:                          var(--font-size-18);
  }

  .offcanvas-body {
    p,
    li {
      color:                            var(--Brand-Primary, #1B294A);
      font-size:                        var(--font-size-14);
      font-weight:                      300;
      line-height:                      140%; /* 19.6px */
      margin-bottom:                    26px;
    }

    .download-dates {
      display:                          flex;
      flex-direction:                   column;
      gap:                              19px;
    }
  }

  .btn {
    font-size:                          var(--font-size-14);
    font-weight:                        700;
    line-height:                        normal;
    letter-spacing:                     4.2px;
    text-align:                         center;
    text-transform:                     uppercase;
    width:                              100%;
    padding:                            20px;

    &.btn-outline-secondary {
      padding:                          20px 18px;
    }
  }

  .offcanvas-footer {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;

    .btn-success {
      color:                            var(--Shades-W, #FFF);
    }
  }
}
</style>
  
  <script>
  import { useUserStore } from '@/store/user'
  
  export default {
    name: 'RapidReportExportOffcanvas',
    data: () => ({
      loading: false,
      startDate: new Date(),
      endDate: new Date(),
      offcanvasStateClass: '',
    }),
    computed: {
      locale() { 
        return this.$utils.datePickerLocale() 
      }
    },
    methods: {
      show() { 
        this.offcanvasStateClass = 'show'; 
      },
      close() { 
        this.offcanvasStateClass = ''
        this.startDate = null
        this.endDate = null
      },
      async doExport() {
        this.loading = true;
        try {
          const userStore = useUserStore();
          const clinicId = userStore.sessionInfo.currentClinic.id;
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          
          const params = {
            clinicId: clinicId,
            startDate: this.startDate.toISOString(),
            endDate: this.endDate.toISOString(),
            DEFAULT_TZ: timezone,
          };

          const data = await this.$reportService.exportQuickReports(params);

          const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
          const link = document.createElement('a');
          link.href = url;
          
          const fileNamePrefix = this.$i18n.t('createClinic.exportQuickReport.exportFileName');
          const dateFormat = this.$i18n.t('fileDateFormat');
          const dateStamp = moment().format(dateFormat);
          link.setAttribute('download', `${fileNamePrefix}_${clinicId}_${dateStamp}.csv`);
          
          document.body.appendChild(link);
          link.click();

          window.URL.revokeObjectURL(url);
          link.remove();

          this.$message.popup(this, 'operationSuccess', 'operationSuccess', 'success')
          this.close();
        } catch (error) {
          console.error("Download error:", error);
          this.$message.popup(this, 'request_notfound_title', 'operationFail', 'error')
        } finally {
          this.loading = false;
        }
      }
    }
  }
  </script>